import apiClient from './apiClient';
import useFileViewerStore from '../../stores/useFileViewerStore';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Prescription PDF API — Generate & download prescription PDFs
 *
 * Endpoints:
 *   GET /api/prescription/visit/:visitId/pdf      — Generate & download PDF
 *   GET /api/prescription/visit/:visitId/preview   — HTML preview
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Generate and download a prescription PDF for a visit.
 * Opens the PDF in a new browser tab for printing/saving.
 *
 * @param {string} visitId - The visit ID
 * @returns {Promise<{blobUrl: string}>} - Blob URL that can be opened/printed
 */
export const generatePrescriptionPdf = async (visitId) => {
  try {
    const response = await apiClient.get(
      `/api/prescription/visit/${visitId}/pdf`,
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    return { blobUrl };
  } catch (error) {
    console.error('[PrescriptionPdfAPI] Error generating PDF:', error);
    throw buildPdfError(error);
  }
};

/**
 * Get a prescription HTML preview for a visit.
 *
 * @param {string} visitId - The visit ID
 * @returns {Promise<{rendered_html: string, layout_meta: object, visit_data_summary: object}>}
 */
export const getPrescriptionPreview = async (visitId) => {
  try {
    const response = await apiClient.get(
      `/api/prescription/visit/${visitId}/preview`
    );
    return response.data;
  } catch (error) {
    console.error('[PrescriptionPdfAPI] Error getting preview:', error);
    throw buildPdfError(error);
  }
};

/**
 * Helper: download PDF and trigger browser save dialog.
 *
 * @param {string} visitId - The visit ID
 * @param {string} [filename] - Optional filename override
 */
export const downloadPrescriptionPdf = async (visitId, filename) => {
  const { blobUrl } = await generatePrescriptionPdf(visitId);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || `prescription_${visitId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
};

/**
 * Helper: open PDF in a new tab for printing.
 *
 * @param {string} visitId - The visit ID
 */
export const openPrescriptionPdf = async (visitId) => {
  const { blobUrl } = await generatePrescriptionPdf(visitId);
  useFileViewerStore.getState().openViewer({
    blobUrl,
    fileType: 'pdf',
    title: 'Prescription',
  });
};


// ── Error helper ─────────────────────────────────────────────────────

function buildPdfError(error) {
  const pdfError = new Error(error.message || 'Failed to generate prescription PDF');

  if (error.response?.status === 400) {
    pdfError.type = 'NO_PRESCRIPTION';
    // ONE message, on the status alone — no string match on the backend's prose.
    // `/prescription/visit/{id}/pdf` raises 400 for exactly one reason (the
    // entry has no medicine, test, advised surgery or diagnosis, i.e. nothing
    // that would appear on the page), so the status IS the meaning here.
    //
    // The copy states the fact instead of instructing the reader to author
    // something: every surface that reaches this endpoint — the patient card,
    // the visit panel, the visit list — is showing an ALREADY-SAVED
    // prescription, and telling its reader to "add a medicine first" was the
    // defect (owner, this task).
    //
    // Note the response body is a Blob here (`responseType: 'blob'` applies to
    // error responses too) and was never readable synchronously, so the old
    // `data?.detail` branch was already dead on this endpoint.
    pdfError.message = 'This prescription has nothing to print.';
  } else if (error.response?.status === 401) {
    pdfError.type = 'UNAUTHORIZED';
    pdfError.message = 'Session expired. Please login again.';
  } else if (error.response?.status === 403) {
    pdfError.type = 'FORBIDDEN';
    pdfError.message = 'You do not have permission to generate prescriptions.';
  } else if (error.response?.status === 404) {
    pdfError.type = 'NOT_FOUND';
    pdfError.message = 'Visit not found.';
  } else if (error.response?.status >= 500) {
    pdfError.type = 'SERVER_ERROR';
    pdfError.message = 'PDF generation failed. Please try again.';
  } else if (!error.response) {
    pdfError.type = 'NETWORK';
    pdfError.message = 'Network error. Please check your connection.';
  } else {
    pdfError.type = 'UNKNOWN';
    pdfError.message = error.message || 'An unexpected error occurred.';
  }

  return pdfError;
}

const prescriptionPdfApi = {
  generatePrescriptionPdf,
  getPrescriptionPreview,
  downloadPrescriptionPdf,
  openPrescriptionPdf,
};

export default prescriptionPdfApi;
